import React, { forwardRef } from 'react';
import { t } from '@sm/intl';
import { uniqueId } from '@wds/utils';
import { useSurveyTheme } from '@sm/webassets';
import useStyles from './useStyles';
import { ProgressBar } from '~app/components/core/ProgressBar';
import { Theme } from '~app/sharedTypes';
import VisuallyHidden from '~app/components/core/VisuallyHidden';

// TODO: Add back defineMessages() when ready to get translated
const COPY = {
  ANSWERED: {
    id: 'PersistentProgressBar.Answered',
    defaultMessage: '{value} of {total} answered',
    description: '[Type: Label][Vis: med] - Shows the total number of questions answered',
  },
  PROGRESS_SR_PREFIX: {
    id: 'PersistentProgressBar.Progress_Sr_Prefix',
    defaultMessage: 'Current Progress,',
    description: '[Type: Label][Vis: med] - Speech readout prefix denoting question progress',
  },
};

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface Props {
  id?: string;
  /** The current value */
  value: number;
  /** The current value in percentage */
  total: number;
  /** Prop to set Right-to-Left */
  isRTL?: boolean;
  /** Used to override the styling */
  className?: string;
}

// No need for use memo since the only time the re-rendering happens is when the value change or theme
// Also very light calculation
const getCurrentValue = (value: number, total: number): number => {
  // Avoid division by 0
  if (total <= 0) {
    return 0;
  }
  // Return the percentage
  return (value / total) * 100;
};

function PersistentProgressBar(
  { id = 'persistent-progress', value, total, isRTL }: Props,
  ref: React.Ref<HTMLDivElement>
): React.ReactElement {
  // TODO: Remove any after the type updates are installed
  const theme: Theme = useSurveyTheme();
  const { progressBarContainer, progressBar, text } = useStyles({ isRTL: !!isRTL });
  const boundedCurrentValue = getCurrentValue(value, total);
  const progressId = uniqueId(`${id}-`);
  const progressLabelId = `${progressId}-label`;
  const progressBarId = `${progressId}-bar`;
  return (
    <div id={progressId} className={progressBarContainer} ref={ref}>
      <span id={progressLabelId} aria-live="polite" aria-atomic className={text}>
        <VisuallyHidden isHidden>{t(COPY.PROGRESS_SR_PREFIX)}</VisuallyHidden>
        {t(COPY.ANSWERED, { value, total })}
      </span>
      <ProgressBar
        id={progressBarId}
        aria-labelledby={progressLabelId}
        value={boundedCurrentValue}
        borderColor={theme.secondaryAccentColor}
        progressColor={theme.secondaryAccentColor}
        radius={5}
        height={8}
        className={progressBar}
      />
    </div>
  );
}

export default forwardRef<HTMLDivElement, Props>(PersistentProgressBar);
